<template>
    <Dialog :visible="showDialog" class="action-popup" :modal="true" :draggable="false">
        <template #container>
            <div class="dialog-header-danger">
                Remove a License
            </div>
            <div class="dialog-content align-items-center">
                <div class="w-full flex justify-content-center align-items-center mb-3">
                    <SvgWarning className="popup-warning-svg" color="var(--popup-warning-color)"/>
                </div>

                <span v-if="user.id == license.admin.id" class="text mb-2 px-2 sm:px-4">Removing this license will make it available for others who know the serial to claim and become the owner of it.</span>
                <span v-else class="text mb-2 px-2 sm:px-4">Removing this license will make you lose the ability to active it.</span>
                <span class="text mb-2 px-2 sm:px-4">Are you sure you want to remove it from your account?</span>

                <div class="content flex flex-column w-full justify-content-center align-items-center mt-2">
                    <DataTable :value="[license]" size="small" tableStyle="min-width: 100%" :row-class="() => 'row'" class="mb-2" scrollable scrollHeight="flex" style="max-height: 25vh; width: 95%;">
                        <Column field="serial_number" header="Serial number">
                            <template #body="slotProps">
                                <span class="text-serial-number">{{slotProps.data.serial_number}}</span>
                            </template>
                        </Column>
                        <Column field="product.application" header="Application" style="max-width: 30%"></Column>
                        <Column field="product.exp" header="Expiration">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.product.exp === '-1'" class="font-italic">None</span>
                                <span v-else>{{format(new Date(slotProps.data.product.exp), 'MMM do, yyyy')}}</span>
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <div v-if="user.id == license.admin.id && license.delegates.length>0" class="mt-4 w-full flex flex-column gap-1">
                    <span style="max-width: 80%;" class="text text-center align-self-center px-2 sm:px-6">{{`This license was assigned to the following user${license.delegates.length>1 ? "s" : ""} which will lose the ability to activate this license in the future.`}}</span>
                    <div class="mt-1 relative w-full" style="height: 2.4rem;">
                        <ScrollDelegatesAvatars :delegates="license.delegates" :justifyContentCenter="license.delegates.length<20"/>
                    </div>
                </div>

                <div v-if="user.id == license.admin.id" class="mt-4 flex flex-column text">
                    <span class="text-center px-2 sm:px-6">Ensure you want to release control before confirming deletion.</span>
                </div>

            </div>

            <div class="dialog-footer">
                <a  class="justify-self-start flex align-items-center justify-content-center" href="https://softron.zendesk.com/hc/en-us/sections/14666349935516-Softron-License-Manager-New-2024-Activations" target="_blank">
                    <Button rounded text style="border-radius: 980px; padding: 0;">
                        <SvgQuestionMarkCircle className="info-svg" color="var(--button-normal-color)"/>
                    </Button>
                </a>
                <div class="buttons">
                    <ButtonModule type="secondary" severity="normal" label="Cancel" @click="$emit('close')"/>
                    <ButtonModule type="primary" severity="danger" label="Remove" @click="showPasswordVerificationDialog = true"/>
                </div>
            </div>
        </template>
    </Dialog>

    <!-- DIALOGS -->

    <!-- Authentication using PASSWORD -->
    <VerificationAuthenticationDialog :showDialog="showPasswordVerificationDialog" :incorrectPassword="wrongPassword" @cancel="showPasswordVerificationDialog = false;" @ok="(val) => verificationAuthenticationDone(val)" :isLoading="httpDeleteSerialNumberLoading"/>
</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, computed, watchEffect } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
// vue
import ButtonModule from '@/components/Modules/ButtonModule.vue'
import VerificationAuthenticationDialog from '@/components/Dialogs/OtherDialogs/VerificationAuthenticationDialog.vue';
import SvgWarning from '@/components/SVGs/SvgWarning.vue';
import ScrollDelegatesAvatars from '@/components/Licenses/Elements/ScrollDelegatesAvatars.vue';
import SvgQuestionMarkCircle from '@/components/SVGs/SvgQuestionMarkCircle.vue';
// Others
import toastService from '@/composables/toastService';
import UserUtils from '@/composables/UserUtils';
import { format } from 'date-fns';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'

const store = useStore()
const router = useRouter()
const { logoutUser } = UserUtils(store, router)

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchDeleteSerialNumber } = useApiRequest()
const httpDeleteSerialNumberLoading = ref(false)
const httpDeleteSerialNumberData = ref(null)

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    license: Object,
})
const { showDialog, license } = toRefs(props)

// --- User info ---
const user = computed(() => store.state.user.user)

// --- EMITS ---
const emit = defineEmits(['close', 'refresh-licenses-table'])

// --- PASSWORD VERIFICATION ---
const showPasswordVerificationDialog = ref(false)
const wrongPassword = ref(false)
// Whenever the dialog opens, it should reset the wrongPassword boolean
watchEffect(() => {
    if (showPasswordVerificationDialog.value) { wrongPassword.value = false }
})
const verificationAuthenticationDone = (password) => {
    // IT CALLS THE SUBMIT REQUEST WITH THE PASSWORD AS THE VERIFICATION
    submitDeleteSerialNumberConfirmed(password)
}

// --- SUBMIT DELETE SERIAL CONFIRMED ---
const submitDeleteSerialNumberConfirmed = async (passwordVerification) => {
    wrongPassword.value = false
    // Send HTTP request with passwordVerification
    httpDeleteSerialNumberLoading.value = true
    try {
        httpDeleteSerialNumberData.value = await fetchDeleteSerialNumber({ password_plain_auth: passwordVerification, serial_number: license.value.serial_number })
        // Reset of values
        showPasswordVerificationDialog.value = false
        wrongPassword.value = false
        // Delete the serial number info from the cache
        store.commit('licenses/removeLicense', httpDeleteSerialNumberData.value.serial_number)
        // Success toast
        //toastService.add({ severity: 'success', summary: "Delete a Serial Number: Success", detail: 'The license has been deleted from yours.', life: 5000 });
        // Close
        emit('close')
    } catch (err) {
        handleDeleteSerialNumberError(err)
    } finally {
        httpDeleteSerialNumberLoading.value = false
    }
}
// Handle an error from the server
const handleDeleteSerialNumberError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Remove a Serial Number: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Remove a Serial Number: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                    toastService.add({ severity: 'warn', summary: "Remove a Serial Number: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Remove a Serial Number: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // WRONG_PASSWORD / ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID / USER_NOT_OWNER / LICENSE_NOT_DELETABLE (401)
                if (errorCode == 'WRONG_PASSWORD') { // WRONG_PASSWORD
                    wrongPassword.value = true
                }
                else if (errorCode == 'USER_NOT_OWNER' || errorCode == 'USER_NOT_OWNER_NOT_DELEGATE') { // USER_NOT_OWNER / USER_NOT_OWNER_NOT_DELEGATE
                    toastService.add({ severity: 'warn', summary: "Remove a Serial Number: Not Authorized.", detail: errorMessage, life: 5000 }); // either because I am not the admin, or because the license is currently in use
                    showPasswordVerificationDialog.value = false
                    emit('close')
                    store.commit('licenses/removeLicense', license.value.serial_number)
                }
                else if (errorCode == 'LICENSE_NOT_DELETABLE') { // LICENSE_NOT_DELETABLE
                    toastService.add({ severity: 'warn', summary: "Remove a Serial Number: Not Authorized.", detail: errorMessage, life: 5000 }); // either because I am not the admin, or because the license is currently in use
                    showPasswordVerificationDialog.value = false
                    emit('close')
                    //store.commit('licenses/removeLicense', license.value.serial_number)
                }
                else if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Remove a Serial Number: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND / SERIAL_INVALID (404)
                if (errorCode == 'SERIAL_INVALID') { // SERIAL_INVALID => errorSerialNumberMessage
                    toastService.add({ severity: 'error', summary: "Remove a Serial Number: Authorization Issue.", detail: 'The serial number is not valid.', life: 5000 });
                    showPasswordVerificationDialog.value = false
                }
                else if (errorCode == 'ACCOUNT_NOT_FOUND' ) { // ACCOUNT_NOT_FOUND => toast + logout
                    toastService.add({ severity: 'error', summary: "Remove a Serial Number: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Remove a Serial Number: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                    toastService.add({ severity: 'error', summary: "Remove a Serial Number: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                    showPasswordVerificationDialog.value = false
                }
                else {  toastService.add({ severity: 'error', summary: `Remove a Serial Number: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Remove a Serial Number: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Remove a Serial Number: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

</script>

<style scoped>
</style>